<template>
<AuthenticatedLayout
    :title="__('Checkout')"
>
    <CheckoutLayout
        :step="step"
    >
        <template #address>
            <div class="sticky top-20">
                <div class="grid grid-cols-1 gap-2">
                    <InputLabel
                        id="billing.email"
                        type="email"
                        :label="__('Email') + ' *'"
                        v-model="form.billing.email"
                        autocomplete="email"
                    />
                    <div class="grid lg:grid-cols-2 gap-2">
                        <InputLabel
                            id="billing.name"
                            :label="__('Name or company name') + ' *'"
                            v-model="form.billing.name"
                            autocomplete="organization"
                        />
                        <InputLabel
                            id="billing.contact_name"
                            :label="__('Name of the contact person') + ' *'"
                            v-model="form.billing.contact_name"
                            autocomplete="name"
                        />
                    </div>

                    <Select
                        :label="__('Country') + ' *'"
                        v-model="form.billing.country"
                        :data="countries"
                        id="billing.country"
                    >
                        <template #display="props">
                            <div class="flex items-center">
                                {{ __('countries.' + props.value) }}
                                <img class="mx-1 w-6 h-6 max-w-max" :src="getLangImage(props.value)">
                            </div>
                        </template>
                        <template #default="props">
                            <Option
                                v-for="(item, key) in props.data"
                                :key="key"
                                @click="props.clicked(key)"
                                class="flex justify-between"
                                color="yellow"
                                :selected="props.active == key"
                            >
                                {{ __('countries.' + key) }}
                                <img class="mx-1 w-6 h-6 max-w-max" :src="getLangImage(key)">
                            </Option>
                        </template>
                    </Select>

                    <InputLabel
                        id="billing.address"
                        :label="__('Street and number of the building / flat') + ' *'"
                        v-model="form.billing.address"
                        autocomplete="street-address"
                    />
                    <InputLabel
                        id="billing.additional_address"
                        :label="__('Additional address information')"
                        v-model="form.billing.additional_address"
                    />
                    <div class="grid grid-cols-3 gap-2">
                        <InputLabel
                            id="billing.phone"
                            :label="__('Phone') + ' *'"
                            v-model="form.billing.phone"
                            autocomplete="tel"
                            class="lg:col-span-1 col-span-3"
                        />
                        <InputLabel
                            id="billing.zip"
                            :label="__('Zip') + ' *'"
                            v-model="form.billing.zip"
                            autocomplete="postal-code"
                        />
                        <InputLabel
                            id="billing.city"
                            :label="__('City') + ' *'"
                            v-model="form.billing.city"
                            autocomplete="address-line2"
                            class="lg:col-span-1 col-span-2"
                        />
                    </div>
                </div>
                <div class="mt-2 text-xs text-gray-400">
                    * - {{__('validation-inline.required')}}
                </div>
            </div>
        </template>
        <template #payments>
            <div class="grid grid-cols-1 gap-2">
                <!-- <button @click="checkoutPay">PAY</button> -->
                <PayPal
                    :total="getTotalPrice(cartItem, coupon, shipping_details).sum"
                    :pay="checkoutPay"
                    @click-payment-button="setPayments"
                />
            </div>
        </template>

        <template #cart>
            <div
                class="grid grid-cols-1 gap-2"
            >
                <div
                    class="grid grid-cols-4 gap-2 pb-2"
                    v-for="(item, key) in cartItem" :key="key"
                    :class="{
                        ['border-b'] : key+1 != cartItem.length
                    }"
                >
                    <div class="flex items-center text-sm col-span-2">
                        <div class="relative hidden w-8 h-8 mr-3 rounded-full md:block">
                            <img
                                class="object-cover w-full h-full rounded-full"
                                :src="item.product.options.text_engrave == true ? item.product_variation.engrave.image.url : item.product.sortedImages[0].link"
                                loading="lazy"
                            >
                            <div class="absolute inset-0 rounded-full shadow-inner" aria-hidden="true"></div>
                        </div>
                        <div>
                            <InertiaLink
                                class="font-bold hover:underline"
                                :href="route('product', item.product.slug)"
                            >{{ item.product.name }}</InertiaLink>
                            <p
                                class="text-xs text-gray-600 dark:text-gray-400"
                                v-html="item.product_variation ? item.product_variation.name : item.product.short_description"
                            ></p>
                        </div>
                    </div>
                    <div class="flex items-end flex-col col-span-2">
                        <span
                            v-if="(user.coupon && user.coupon.products.find(element => element.id == item.product_id)) ||
                                (coupon && coupon.products.find(element => element.id == item.product_id) )"
                            class="mb-1 p-1 text-xs w-min ml-auto font-bold text-red-700 bg-red-100 rounded-full whitespace-nowrap line-through"
                        >
                            {{ getOriginalPrice(item) }}
                        </span>
                        <span
                            class="px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100 whitespace-nowrap"
                        >
                            {{ getTotalPerProduct(item, true, coupon) }}
                        </span>
                    </div>
                </div>
                <div
                    class="grid grid-cols-4 gap-2"
                >
                    <div
                        v-if="(
                                getCountDiscount(
                                    cartItem,
                                    coupon,
                                    getTotalPrice(cartItem, coupon, shipping_details).discountCart) > 0 &&
                                (coupon || user.coupon)
                            ) ||
                            getTotalPrice(cartItem, coupon, shipping_details).discountCart > 0"
                        class="flex items-center justify-end text-base col-start-3 col-span-2"
                    >
                        <p class="text-xs font-bold">{{__('Discount')}}</p>
                        <span
                            class="ml-2 p-1 text-xs w-min font-bold text-red-700 bg-red-100 rounded-full whitespace-nowrap"
                        >
                            -{{
                                getPrice(
                                    getCountDiscount(
                                        cartItem,
                                        coupon,
                                        getTotalPrice(cartItem, coupon, shipping_details).discountCart
                                    )
                                )
                            }}
                        </span>
                    </div>
                    <div
                        class="flex items-center justify-end text-base col-start-3 col-span-2"
                    >
                        <p class="text-xs font-bold">{{__('Shipping')}}</p>
                        <span
                            class="ml-2 p-1 text-xs w-min font-bold text-green-700 bg-green-100 rounded-full whitespace-nowrap"
                        >
                            {{ !getTotalPrice(cartItem, coupon, shipping_details).free_shipping ? __( getPrice(shipping_details.price ? shipping_details.price.value : 0) ) : __('Free') }}
                        </span>
                    </div>
                </div>

                <div class="grid grid-cols-4 gap-2">
                    <div class="lg:col-start-3 md:col-start-2 lg:col-span-2 md:col-span-3 col-span-4 text-xl flex items-center justify-end border-t pt-2">
                        <div>{{ __('Total') }}:</div>
                        <div class="font-semibold bg-red-700 text-gray-200 rounded-md py-1 px-2 ml-2">
                            {{ getPrice( getTotalPrice(cartItem, coupon, shipping_details).sum ) }}
                            </div>
                    </div>
                </div>
            </div>
        </template>

    </CheckoutLayout>

    <template #footerAddon>
        <div class="flex lg:space-x-3 space-y-3 lg:flex-row flex-col">
            <div class="flex lg:flex-none flex-wrap lg:flex-col flex-row space-y-2 lg:w-32 w-full">
                <div
                    class="lg:h-auto h-24"
                    v-for="shipping in shipping.delivery" :key="shipping.id"
                >
                    <img class="object-contain max-w-full max-h-full mx-auto" :src="shipping.image" />
                </div>
            </div>
            <div class="ql-snow">
                <div class="ql-editor content" style="padding: 0 !important;" v-html="shipping.description ? shipping.description.value : ''"></div>
            </div>
        </div>
    </template>

</AuthenticatedLayout>
</template>

<script>
import CheckoutLayout from '@/Layouts/Checkout';
import AuthenticatedLayout from '@/Layouts/Authenticated';
import SvgIcon from 'UI/SvgIcon';
import Heading from 'COMP/Heading';
import CustomSelect from 'UI/CustomSelect';
import InputLabel from 'UI/InputLabel';
import Steps from 'STEPS/Container';
import StepItem from 'STEPS/Item';

import { ref } from '@vue/reactivity';
import Button from 'UI/Button';
import Checkbox from 'UI/Checkbox';

import PayPal from 'COMP/Payments/PayPal';
import Select from 'COMP/CustomSelect/Select';
import Option from 'COMP/CustomSelect/Option';

import {
    pricePerItem,
    getTotalPerProduct,
    getPrice,
    getFullName,
    getAddress,
    checkError,
    inArray,

    getTotalPrice,
    getOriginalPrice,
    getCountDiscount,
    getLangImage,
} from 'helper';

import { computed, provide, watch } from '@vue/runtime-core';
import { useForm, usePage } from '@inertiajs/inertia-vue3';
import axios from 'axios';

export default {
    components: {
        AuthenticatedLayout,
        CheckoutLayout,
        SvgIcon,
        Heading,
        CustomSelect,
        InputLabel,
        Button,
        Steps,
        StepItem,
        Checkbox,

        PayPal,
        Select,
        Option
    },

    props: {
        auth: Object,
        loggedin: Boolean,
        step: Number,
        cartItem: [Array, Object],
        shipping: [Array, Object],
        coupon: [Object, Array],
        shipping_desc: [Array, Object],
        countries: [Array, Object],
    },
    setup(props) {
        const selected = ref([]);
        const user = computed(() => usePage().props.value.auth.user);
        const shipping_details = ref(props.shipping);

        const form = useForm({
            invoice: true,
            payments: '',
            total: 0,
            billing: {
                email: props.loggedin ? props.auth.user.email : '',
                name: '',
                contact_name: props.loggedin ? getFullName(props.auth.user) : '',
                address: props.loggedin ? getAddress(props.auth.user) : '',
                additional_address: '',
                phone: props.loggedin ? props.auth.user.phone : '',
                zip: props.loggedin ? props.auth.user.zip : '',
                city: props.loggedin ? props.auth.user.city : '',
                country: null,
            },
            cart: props.cartItem,
            payment_id: null,
            transaction_id: null,
        });

        watch(() => form.billing.country, (val) => {
            axios
            .post(route('checkout.check'), {
                country: val,
                type: 'shipping'
            })
            .then((response) => {
                shipping_details.value.price = response.data.shipping;
                shipping_details.value.price_free = response.data.free_shipping;
            })


        })


        provide('form', form);

        const paypal = {
            sandbox:    'ASjzNvHAHMzMq7U58FElSqXnCQRTtuJZH2RUh8u72MAloDsjMaoAWQXT76h4zY-4cpmeuK6Wu_DSjF6R',
            production: 'AcyU-Hd9zGm4N-pe9MlsVN9mx48kWUjUResovKmKdMBGsqTh7XAfaB8qohZ6m9Wd0ufhqcDHA8zbfY02'
        }

        function checkoutPay(id, transaction_id = null){
            if(transaction_id) {

            }
            form
            .transform((data) => ({
                ...data,
                total: getTotalPrice(data.cart, props.coupon, props.shipping).sum,
                payment_id: id,
                transaction_id: transaction_id
            }))
            .post(route('checkout.pay', {coupon : props.coupon ? props.coupon.id : null}), {
                preserveScroll: true,
            })
        }

        const discountCart = ref(null);

        function setPayments(ev) {
            form.payments = ev.fundingSource;
        }

        return {
            selected,
            user,
            shipping_details,

            checkoutPay,

            form,
            getTotalPrice,
            paypal,

            discountCart,
            setPayments,

            getPrice,

            pricePerItem,
            getTotalPerProduct,
            getAddress,
            checkError,
            inArray,
            getCountDiscount,
            getOriginalPrice,
            getLangImage,
        }
    },
}
</script>
